import React, { useState } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Checkbox, Input } from "rsuite";
import { getClientLogo } from "../../../../utils/clientLogos";
import { postCorridorAction } from "../../../../api";
import InfoCircleIcon from "../../../../assets/img/action-center/info-circle-blue.svg";
import CorridorSuccessModal from "./CorridorSuccessModal";
import "./CorridorModal.scss";

// Color palette for client icon fallbacks
const CLIENT_COLORS = [
  "#3B82F6", // Blue
  "#F97316", // Orange
  "#8B5CF6", // Purple
  "#10B981", // Green
  "#EC4899", // Pink
  "#14B8A6", // Teal
];

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "-";
  return `$${Math.round(value).toLocaleString()}`;
};

const CorridorModal = ({ data = [], propertyId, onClose, onSuccess }) => {
  const [selectedClients, setSelectedClients] = useState(() =>
    data.map((client) => client.id)
  );
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleClientToggle = (clientId, checked) => {
    if (checked) {
      setSelectedClients((prev) => [...prev, clientId]);
    } else {
      setSelectedClients((prev) => prev.filter((id) => id !== clientId));
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const payload = {
        propertyId,
        actionType: "CONFIRM",
        comment: comments,
        items: data.map((client) => ({
          recommendationId: client.id,
          selected: selectedClients.includes(client.id),
        })),
      };

      await postCorridorAction(payload);
      setShowSuccessModal(true);
    } catch {
      // Let the user retry on failure
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    onSuccess(selectedClients);
  };

  return (
    <>
      <Modal
        className="corridor-modal"
        open={true}
        size="sm"
        backdrop="static"
        onClose={onClose}
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>Unlock demand in high-value markets</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="corridor-modal-info">
            <img src={InfoCircleIcon} alt="" className="info-icon" />
            <span>
              Please select which Buyers you&apos;d like to partner with.
            </span>
          </div>

          <div className="corridor-modal-clients">
            {data.map((client, index) => {
              const isSelected = selectedClients.includes(client.id);
              return (
                <div
                  key={client.id}
                  data-testid={`client-row-${client.id}`}
                  className={`corridor-modal-client-row${isSelected ? " selected" : ""}`}
                >
                  <div className="client-checkbox">
                    <Checkbox
                      checked={isSelected}
                      onChange={(_, checked) =>
                        handleClientToggle(client.id, checked)
                      }
                      aria-label={client.clientName}
                    />
                  </div>
                  <div className="client-content">
                    <div className="client-row-top">
                      <div className="client-info">
                        {getClientLogo(client.clientName) ? (
                          <img
                            src={getClientLogo(client.clientName)}
                            alt={client.clientName}
                            className="client-icon"
                          />
                        ) : (
                          <span
                            data-testid={`client-fallback-${client.id}`}
                            className="client-icon client-icon-fallback"
                            style={{
                              backgroundColor: CLIENT_COLORS[index % 6],
                            }}
                          />
                        )}
                        <span className="client-name">{client.clientName}</span>
                      </div>
                    </div>
                    <div className="client-stats">
                      <div className="stat">
                        <span className="stat-label">Avg. Lead Time</span>
                        <span className="stat-value">
                          {client.avgLeadTimeClient != null
                            ? `${client.avgLeadTimeClient} days`
                            : "-"}
                        </span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Avg. Length of Stay</span>
                        <span className="stat-value">
                          {client.avgLosClient != null
                            ? `${client.avgLosClient} days`
                            : "-"}
                        </span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Avg. ADR</span>
                        <span className="stat-value">
                          {formatCurrency(client.avgTransactionalValueClient)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="corridor-modal-averages-note">
            * Averages of past 90 days&apos; bookings across Hotel Trader
            (updates every 3 days).
          </p>

          <div className="corridor-modal-comments">
            <label htmlFor="corridor-comments">Additional comments</label>
            <Input
              as="textarea"
              id="corridor-comments"
              rows={3}
              value={comments}
              onChange={setComments}
              placeholder="Anything else you'd like us to know?"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="ghost"
            className="cancel-btn"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            appearance="primary"
            className="continue-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Continue
          </Button>
        </Modal.Footer>
      </Modal>

      {showSuccessModal && (
        <CorridorSuccessModal onClose={handleSuccessClose} />
      )}
    </>
  );
};

CorridorModal.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      clientId: PropTypes.number,
      clientName: PropTypes.string.isRequired,
      market: PropTypes.string,
      clientGmvLast30Days: PropTypes.number,
      clientGmvLast90Days: PropTypes.number,
      clientBookingVolumeLast30Days: PropTypes.number,
      clientBookingVolumeLast90Days: PropTypes.number,
      avgLeadTimeClient: PropTypes.number,
      avgLosClient: PropTypes.number,
      avgTransactionalValueClient: PropTypes.number,
    })
  ).isRequired,
  propertyId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default CorridorModal;
